import { toast } from "sonner";
import React, { useState, useEffect, useCallback } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DocumentScanner from "@/components/invoices/DocumentScanner";
import InvoiceOCRReview from "@/components/invoices/InvoiceOCRReview";
import StockMatcher from "@/components/invoices/StockMatcher";
import InvoiceDetail from "@/components/invoices/InvoiceDetail";
import DiagnosticsPanel from "@/components/invoices/DiagnosticsPanel";
import TestInvoiceSuite from "@/components/invoices/TestInvoiceSuite";
import { invoiceLog } from "@/components/invoices/invoiceLogger";
import {
  ScanLine, Plus, Search, FileText, CheckCircle2,
  AlertTriangle, Clock, Package, ChevronRight, Loader2,
  BarChart3, RefreshCw
} from "lucide-react";
import moment from "moment";

// ─── Step config ──────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: "Escaneig",      short: "Scan"  },
  { id: 1, label: "Revisió OCR",   short: "OCR"   },
  { id: 2, label: "Estoc",         short: "Estoc" },
  { id: 3, label: "Completat",     short: "OK"    },
];

function StepBar({ current }) {
  return (
    <div className="flex items-center mb-6 select-none">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
            i === current ? "bg-[#7B2D3F] text-white shadow-md shadow-[#7B2D3F]/20"
            : i < current ? "bg-emerald-100 text-emerald-700"
            : "bg-gray-100 text-gray-400"
          }`}>
            {i < current
              ? <CheckCircle2 className="w-3 h-3" />
              : <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                  i === current ? "border-white/40 bg-white/20" : "border-current"
                }`}>{i + 1}</span>
            }
            <span className="hidden sm:inline">{s.label}</span>
            <span className="sm:hidden">{s.short}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 rounded-full transition-colors ${i < current ? "bg-emerald-300" : "bg-gray-200"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const STATUS_CFG = {
  draft:          { label: "Esborrany", cls: "bg-gray-100 text-gray-600" },
  pending_review: { label: "Pendent",   cls: "bg-amber-100 text-amber-700" },
  reviewed:       { label: "Revisat",   cls: "bg-blue-100 text-blue-700" },
  confirmed:      { label: "Confirmat", cls: "bg-emerald-100 text-emerald-700" },
  stock_updated:  { label: "Estoc ✓",  cls: "bg-purple-100 text-purple-700" },
};

function ProcessingOverlay({ step }) {
  const msgs = [
    { title: "Pujant imatge i executant OCR amb IA…", sub: "Claude Sonnet analitza la factura" },
    { title: "Guardant dades estructurades…", sub: "" },
    { title: "Actualitzant estoc…", sub: "" },
  ];
  const msg = msgs[step] || msgs[0];
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl bg-[#7B2D3F]/10 flex items-center justify-center">
          <ScanLine className="w-7 h-7 text-[#7B2D3F]" />
        </div>
        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#7B2D3F] rounded-full flex items-center justify-center">
          <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
        </div>
      </div>
      <div className="text-center">
        <p className="font-bold text-gray-900">{msg.title}</p>
        {msg.sub && <p className="text-sm text-gray-500 mt-1">{msg.sub}</p>}
      </div>
      <div className="flex gap-1">
        {[0,1,2].map(i => (
          <div key={`step-${i}`} className="w-2 h-2 rounded-full bg-[#7B2D3F]/30 animate-pulse" style={{ animationDelay: `${i*200}ms` }} />
        ))}
      </div>
    </div>
  );
}

export default function InvoiceScanner() {
  const { currentVenue, user } = useVenue();
  const [view, setView]   = useState("list"); // list | new | detail
  const [step, setStep]   = useState(0);
  const [invoices, setInvoices]     = useState([]);
  const [selectedInvoice, setSelectedInvoice]   = useState(null);
  const [invoiceMovements, setInvoiceMovements] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [processing, setProcessing] = useState(false);
  const [search, setSearch]   = useState("");
  const [showTools, setShowTools] = useState(false);

  // Pipeline state
  const [scannedPages, setScannedPages]   = useState([]);
  const [ocrData, setOcrData]             = useState(null);
  const [savedInvoiceId, setSavedInvoiceId] = useState(null);
  const [diagnostics, setDiagnostics]     = useState(null);

  const fetchInvoices = useCallback(() => {
    if (!currentVenue) return;
    setLoading(true);
    hosys.entities.Invoice.filter({ venue_id: currentVenue.id }, "-created_date", 100)
      .then(setInvoices)
      .catch(e => invoiceLog.error("list", "Failed to fetch invoices", { error: e.message }))
      .finally(() => setLoading(false));
  }, [currentVenue]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // ─── Step 0 → 1: Scan done ─────────────────────────────────────────────────
  const handleScanDone = async ({ pages, originalFile, processedFile, processedDataUrl, scanMode }) => {
    setScannedPages(pages || []);
    setProcessing(true);
    invoiceLog.info("pipeline", "Scan done, uploading and running OCR", { pages: pages?.length || 1 });

    try {
      // Upload primary processed image (for OCR)
      const procRes = await hosys.integrations.Core.UploadFile({ file: processedFile });
      const origRes = await hosys.integrations.Core.UploadFile({ file: originalFile });

      // Upload all pages if multi-page
      const pageUrls = [procRes.file_url];
      if (pages && pages.length > 1) {
        for (let i = 1; i < pages.length; i++) {
          const r = await hosys.integrations.Core.UploadFile({ file: pages[i].processedFile });
          pageUrls.push(r.file_url);
        }
      }

      // Create draft invoice record
      const draft = await hosys.entities.Invoice.create({
        venue_id: currentVenue.id,
        status: "pending_review",
        scan_mode: scanMode,
        original_file_url: origRes.file_url,
        processed_file_url: procRes.file_url,
        uploaded_by: user?.email || "",
      });
      setSavedInvoiceId(draft.id);
      invoiceLog.info("pipeline", "Draft invoice created", { id: draft.id });

      // Run OCR (multi-page aware)
      const ocrRes = await hosys.functions.invoke("extractInvoiceOCR", {
        image_url: procRes.file_url,
        image_urls: pageUrls,
        venue_id: currentVenue.id,
      });

      const ocrPayload = ocrRes.data?.data || ocrRes.data;
      const diag = ocrRes.data?.diagnostics;
      if (diag) setDiagnostics(diag);

      invoiceLog.info("pipeline", "OCR complete", {
        confidence: ocrPayload?.ocr_confidence,
        items: ocrPayload?.items?.length,
        diagnosticsMs: diag?.totalMs,
      });

      setOcrData({
        ...ocrPayload,
        processedFileUrl: procRes.file_url,
        pages: pages?.map((p, i) => ({ processedUrl: pageUrls[i] || procRes.file_url })) || [],
      });
      setStep(1);
    } catch (e) {
      invoiceLog.error("pipeline", "Scan pipeline failed", { error: e.message });
      toast.error("Error processant la imatge: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  // ─── Load test invoice directly ─────────────────────────────────────────────
  const handleLoadTestInvoice = (testCase) => {
    invoiceLog.info("test", `Loading test case: ${testCase.id}`);
    setOcrData({
      ...testCase.mockOCRData,
      processedFileUrl: null,
      pages: [],
      _isTestCase: true,
    });
    setStep(1);
    setView("new");
  };

  // ─── Step 1 → 2: OCR reviewed ───────────────────────────────────────────────
  const handleOCRConfirm = async (reviewedData) => {
    setProcessing(true);
    try {
      if (savedInvoiceId) {
        await hosys.entities.Invoice.update(savedInvoiceId, {
          supplier_name:    reviewedData.supplier_name,
          supplier_nif:     reviewedData.supplier_nif,
          invoice_number:   reviewedData.invoice_number,
          invoice_date:     reviewedData.invoice_date,
          accounting_date:  reviewedData.accounting_date,
          payment_terms:    reviewedData.payment_terms,
          subtotal:         reviewedData.subtotal,
          vat_amount:       reviewedData.vat_amount,
          surcharge_amount: reviewedData.surcharge_amount,
          retention_amount: reviewedData.retention_amount,
          total:            reviewedData.total,
          tax_lines:        JSON.stringify(reviewedData.tax_lines || []),
          items:            JSON.stringify(reviewedData.items || []),
          ocr_confidence:   reviewedData.ocr_confidence,
          vat_valid:        reviewedData.vat_valid,
          vat_warning:      reviewedData.vat_warning,
          status:           "reviewed",
          reviewed_by:      user?.email || "",
          reviewed_at:      new Date().toISOString(),
        });
      }
      setOcrData(prev => ({ ...prev, ...reviewedData }));
      setStep(2);
      invoiceLog.info("pipeline", "OCR data confirmed, moving to stock step");
    } catch (e) {
      invoiceLog.error("pipeline", "Failed to save OCR data", { error: e.message });
      toast.error("Error guardant dades: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  // ─── Step 2 → 3: Stock confirmed ────────────────────────────────────────────
  const handleStockConfirm = async (movements) => {
    setProcessing(true);
    try {
      if (movements.length > 0) await hosys.entities.StockMovement.bulkCreate(movements);
      if (savedInvoiceId) {
        await hosys.entities.Invoice.update(savedInvoiceId, {
          status: movements.length > 0 ? "stock_updated" : "confirmed",
          confirmed_by: user?.email || "",
          confirmed_at: new Date().toISOString(),
        });
      }
      setStep(3);
      fetchInvoices();
      invoiceLog.info("pipeline", `Pipeline complete. ${movements.length} movements created.`);
    } catch (e) {
      invoiceLog.error("pipeline", "Stock update failed", { error: e.message });
      toast.error("Error actualitzant estoc: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleStockSkip = async () => {
    setProcessing(true);
    try {
      if (savedInvoiceId) {
        await hosys.entities.Invoice.update(savedInvoiceId, {
          status: "confirmed",
          confirmed_by: user?.email || "",
          confirmed_at: new Date().toISOString(),
        });
      }
      setStep(3);
      fetchInvoices();
    } catch (e) {
      toast.error("Error: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const openDetail = async (invoice) => {
    setSelectedInvoice(invoice);
    const movs = await hosys.entities.StockMovement.filter({ invoice_id: invoice.id });
    setInvoiceMovements(movs);
    setView("detail");
  };

  const startNew = () => {
    setStep(0); setScannedPages([]); setOcrData(null); setSavedInvoiceId(null); setDiagnostics(null);
    invoiceLog.clear();
    setView("new");
  };

  const filtered = invoices.filter(inv =>
    !search ||
    (inv.supplier_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (inv.invoice_number || "").toLowerCase().includes(search.toLowerCase())
  );

  // ─── DETAIL VIEW ──────────────────────────────────────────────────────────
  if (view === "detail" && selectedInvoice) {
    return (
      <div className="max-w-3xl mx-auto py-2">
        <InvoiceDetail
          invoice={selectedInvoice}
          movements={invoiceMovements}
          onBack={() => { setView("list"); fetchInvoices(); }}
        />
      </div>
    );
  }

  // ─── NEW INVOICE FLOW ─────────────────────────────────────────────────────
  if (view === "new") {
    return (
      <div className="max-w-3xl mx-auto py-2 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView("list")}
            className="text-sm text-gray-400 hover:text-gray-700 flex items-center gap-1 transition-colors"
          >
            ← Tornar
          </button>
          <h1 className="text-xl font-bold text-gray-900">Nova factura</h1>
        </div>

        <StepBar current={step} />

        {/* Server diagnostics hint */}
        {diagnostics && step >= 1 && (
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs text-gray-500">
            <span className="text-emerald-500 font-bold">✓</span>
            OCR complet en {diagnostics.totalMs}ms · {diagnostics.pageCount} pàgina(s) · Session {diagnostics.sessionId}
          </div>
        )}

        {processing ? (
           <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
             <ProcessingOverlay step={step} />
           </div>
         ) : (
           <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
             {step === 0 && (
               <DocumentScanner onProcessed={handleScanDone} onCancel={() => setView("list")} />
             )}
             {step === 1 && ocrData ? (
               <InvoiceOCRReview
                 data={ocrData}
                 processedImageUrl={ocrData.processedFileUrl}
                 pages={ocrData.pages}
                 venueId={currentVenue?.id}
                 onConfirm={handleOCRConfirm}
                 onBack={() => setStep(0)}
               />
             ) : step === 1 ? (
               <div className="text-center py-16 text-gray-500">Carregant dades OCR...</div>
             ) : null}
             {step === 2 && ocrData && (
               <StockMatcher
                 items={ocrData.items || []}
                 invoiceData={ocrData}
                 venueId={currentVenue?.id}
                 currentUser={user}
                 onConfirm={handleStockConfirm}
                 onSkip={handleStockSkip}
               />
             )}
             {step === 3 && (
               <div className="text-center py-12 space-y-4">
                 <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                   <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                 </div>
                 <h3 className="text-xl font-bold text-gray-900">Factura processada!</h3>
                 <p className="text-gray-500 max-w-sm mx-auto">
                   La factura s'ha guardat i l'estoc s'ha actualitzat automàticament.
                 </p>
                 <div className="flex gap-3 justify-center flex-wrap">
                   <Button variant="outline" onClick={() => setView("list")}>Veure llistat</Button>
                   <Button className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white" onClick={startNew}>
                     <Plus className="w-4 h-4 mr-2" /> Nova factura
                   </Button>
                 </div>
               </div>
             )}
           </div>
         )}

        {/* Tools section (diagnostics + test suite) */}
        <div className="space-y-3">
          <DiagnosticsPanel />
          {step === 0 && (
            <TestInvoiceSuite onLoadTestInvoice={handleLoadTestInvoice} />
          )}
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScanLine className="w-6 h-6 text-[#7B2D3F]" /> Escàner de Factures
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Digitalitza, extreu IVA i actualitza l'estoc automàticament
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={fetchInvoices} className="h-9 gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Actualitzar</span>
          </Button>
          <Button onClick={startNew} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-2">
            <Plus className="w-4 h-4" /> Nova factura
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total factures",  value: invoices.length,                                          icon: FileText,    color: "text-blue-600 bg-blue-50" },
          { label: "Pendents revisió",value: invoices.filter(i => i.status === "pending_review").length, icon: Clock,       color: "text-amber-600 bg-amber-50" },
          { label: "Confirmades",     value: invoices.filter(i => ["confirmed","stock_updated"].includes(i.status)).length, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
          { label: "Estoc actualitzat",value: invoices.filter(i => i.status === "stock_updated").length, icon: Package,     color: "text-purple-600 bg-purple-50" },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.color}`}>
              <stat.icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500 font-medium leading-tight truncate">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900 leading-tight">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Cercar per proveïdor o número de factura..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Invoice list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#7B2D3F] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <ScanLine className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-600 font-semibold">Cap factura registrada</p>
          <p className="text-sm text-gray-400 mb-5">Fes clic a "Nova factura" per escanejar la primera</p>
          <Button onClick={startNew} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-2">
            <Plus className="w-4 h-4" /> Escanejar factura
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="divide-y divide-gray-50">
            {filtered.map(inv => {
              const st = STATUS_CFG[inv.status] || STATUS_CFG.draft;
              return (
                <button
                  key={inv.id}
                  onClick={() => openDetail(inv)}
                  className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50/70 transition-colors text-left gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/8 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#7B2D3F]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate text-sm">
                        {inv.supplier_name || "Proveïdor desconegut"}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Nº {inv.invoice_number || "—"} · {inv.invoice_date || moment(inv.created_date).format("DD/MM/YYYY")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {inv.vat_valid === false && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                    <div className="text-right hidden sm:block">
                      <p className="font-bold text-gray-900 text-sm">{(inv.total || 0).toFixed(2)}€</p>
                      <p className="text-xs text-gray-400">IVA: {(inv.vat_amount || 0).toFixed(2)}€</p>
                    </div>
                    <Badge className={`${st.cls} border-0 text-xs whitespace-nowrap`}>{st.label}</Badge>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tools always accessible from list */}
      <div className="space-y-3 pt-2">
        <button
          onClick={() => setShowTools(!showTools)}
          className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors font-medium"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          {showTools ? "Amagar" : "Mostrar"} eines de diagnòstic i proves
        </button>
        {showTools && (
          <div className="space-y-3">
            <DiagnosticsPanel />
            <TestInvoiceSuite onLoadTestInvoice={(tc) => { startNew(); setTimeout(() => handleLoadTestInvoice(tc), 100); }} />
          </div>
        )}
      </div>
    </div>
  );
}